import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClients, ApiClientConfig } from '@/api/client';
import type { components as ReportingComponents } from '@/api/types/reporting-service';
import type { components as AssetComponents } from '@/api/types/asset-service';
import { apiFetch } from '@/app/api-fetch';

// Per-screen query layer for S1 Attention. Keep these isolated from the
// generic typed-client surface so the screen file stays declarative.

export type HealthHistory = AssetComponents['schemas']['HealthHistoryResponse'];
export type AttentionQueueItem =
  ReportingComponents['schemas']['AttentionQueueItem'];

type TopologyShape = {
  nodes: { id: string; label: string; kind?: string }[];
  focusId: string;
  impactedIds: string[];
};

// Per ADR 0009 reporting-service endpoints take the per-service typed
// client rather than the gateway-composite one. The MSW handler in dev
// shadows /v1/attention/queue with the same wire shape so this path
// works both online (real backend) and offline (mock).
export function useAttentionQueue(clients: ApiClients) {
  return useQuery({
    queryKey: ['attention', 'queue'],
    queryFn: async (): Promise<AttentionQueueItem[]> => {
      const { data, error } = await clients.reporting.GET(
        '/v1/attention/queue',
        { params: { query: { limit: 7 } } },
      );
      if (error || !data) {
        throw new Error(
          (error as { error?: string } | undefined)?.error ??
            'attention queue fetch failed',
        );
      }
      return data.queue ?? [];
    },
    staleTime: 30_000,
  });
}

export function useAssetHealthHistory(
  clients: ApiClients,
  assetId: string | undefined,
) {
  return useQuery({
    enabled: !!assetId,
    queryKey: ['asset', assetId, 'health-history', 30],
    queryFn: async (): Promise<HealthHistory> => {
      const { data, error } = await clients.asset.GET(
        '/v1/assets/{assetId}/health-history',
        {
          params: {
            path: { assetId: assetId! },
            query: { days: 30, bucket: 'day' },
          },
        },
      );
      if (error || !data) {
        throw new Error(
          (error as { error?: string } | undefined)?.error ??
            'health-history failed',
        );
      }
      return data;
    },
    staleTime: 60_000,
  });
}

export function useAssetTopology(
  config: ApiClientConfig,
  assetId: string | undefined,
) {
  return useQuery({
    enabled: !!assetId,
    queryKey: ['asset', assetId, 'topology'],
    queryFn: async (): Promise<TopologyShape | null> => {
      // Topology is a Phase 3 (2..N) deferred endpoint — until it ships,
      // we hit the MSW handler at /v1/assets/:id/topology. The shape
      // returned is structurally the same as what the eventual real
      // endpoint will return per backend.md.
      const res = await apiFetch(config, `/v1/assets/${assetId}/topology`);
      if (res.status === 404) return null;
      return (await res.json()) as TopologyShape;
    },
    staleTime: 60_000,
  });
}

export function useAcknowledgeAlert(config: ApiClientConfig) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { alertId: string }) => {
      const res = await apiFetch(
        config,
        `/v1/alerts/${vars.alertId}/acknowledge`,
        { method: 'POST' },
      );
      if (!res.ok) {
        throw new Error('acknowledge failed');
      }
      return (await res.json()) as { alert_id: string; status: string };
    },
    // Optimistic update: flip the queue item's open_alerts down by 1 and
    // record an acknowledged marker; rollback on failure.
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['attention', 'queue'] });
      const prev = qc.getQueryData<AttentionQueueItem[]>(['attention', 'queue']);
      if (prev) {
        qc.setQueryData<AttentionQueueItem[]>(
          ['attention', 'queue'],
          prev.map((item) =>
            item.alert_id === vars.alertId
              ? { ...item, open_alerts: Math.max(0, item.open_alerts - 1) }
              : item,
          ),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(['attention', 'queue'], ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['attention', 'queue'] });
    },
  });
}

export function useSnoozeAlert(config: ApiClientConfig) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { alertId: string; hours: number }) => {
      const res = await apiFetch(
        config,
        `/v1/alerts/${vars.alertId}/snooze`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hours: vars.hours }),
        },
      );
      if (!res.ok) {
        throw new Error('snooze failed');
      }
      return (await res.json()) as { alert_id: string; snoozed_until: string };
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['attention', 'queue'] });
    },
  });
}
